import { useCallback, useEffect, useState } from 'react';
import { Link } from 'wouter';

import {
  allowlistedVerificationUri,
  disconnectConnection,
  listConnections,
  readConnectionSignIn,
  reconnectConnection,
  signOutOfConnection,
  startConnectionSignIn,
} from '../../api/connections';
import type {
  ConnectionRow,
  ConnectionState,
  SignInAttempt,
  SubscriptionLane,
} from '../../api/connections';

const LANES: { id: SubscriptionLane; label: string }[] = [
  { id: 'claude', label: 'Claude Code' },
  { id: 'chatgpt_codex', label: 'ChatGPT / Codex' },
  { id: 'antigravity_gemini', label: 'Antigravity / Gemini CLI' },
];

const STATUS_FRESHNESS_MS = 10 * 60 * 1000;

function visibleConnection(row: ConnectionRow, now: number): ConnectionRow {
  const checked = row.checked_at ? Date.parse(row.checked_at) : NaN;
  const fresh = Number.isFinite(checked) && checked <= now && now - checked < STATUS_FRESHNESS_MS;
  if (fresh) return row;
  return {
    ...row,
    state: row.state === 'connected' ? 'stale' : row.state,
    last_known_state: row.last_known_state || row.state,
    last_known_age_seconds: Number.isFinite(checked) ? Math.max(0, Math.floor((now - checked) / 1000)) : null,
    quota: null,
    quota_status: 'not_measured',
  };
}

const STATE_LABELS: Record<ConnectionState, string> = {
  not_connected: 'Not connected',
  signing_in: 'Sign-in in progress',
  stale: 'Stale',
  unverifiable: 'Unverifiable',
  expired: 'Sign-in needs renewal',
  connected: 'Connected',
};

const HISTORY_LABELS: Record<ConnectionState, string> = {
  not_connected: 'no sign-in was reported',
  signing_in: 'a sign-in was in progress',
  stale: 'the previous evidence was old',
  unverifiable: 'Auteur could not confirm the sign-in',
  expired: 'the sign-in needed renewal',
  connected: 'a sign-in was previously confirmed',
};

function lastCheckedCopy(row: ConnectionRow): string {
  const age = typeof row.last_known_age_seconds === 'number' && Number.isFinite(row.last_known_age_seconds)
    ? Math.max(0, row.last_known_age_seconds || 0)
    : row.checked_at
      ? Math.max(0, Math.floor((Date.now() - Date.parse(row.checked_at)) / 1000))
      : 0;
  const minutes = Math.floor(age / 60);
  const previous = row.last_known_state ? HISTORY_LABELS[row.last_known_state] : 'there was no recent evidence';
  return 'Last checked ' + minutes + ' min ago. Last known: ' + previous + '.';
}

function isPolling(status: SignInAttempt['status']): boolean {
  return status === 'queued' || status === 'in_progress' || status === 'awaiting_user';
}

function LaneCard({
  row,
  attempt,
  busy,
  pairingInNewTab,
  onSignIn,
  onSignOut,
  onToggleConnection,
}: {
  row: ConnectionRow;
  attempt?: SignInAttempt;
  busy: boolean;
  pairingInNewTab: boolean;
  onSignIn: (lane: SubscriptionLane) => void;
  onSignOut: (lane: SubscriptionLane) => void;
  onToggleConnection: (lane: SubscriptionLane, enabled: boolean) => void;
}) {
  const laneLabel = LANES.find((lane) => lane.id === row.lane)?.label || row.lane;
  const verificationLink = attempt?.relay
    ? allowlistedVerificationUri(row.lane, attempt.relay.verification_uri)
    : null;
  const noNode = row.pairing_required || !row.node;

  return (
    <article className="settings-card subscription-card">
      <div className="settings-card-heading">
        <div>
          <h3>{laneLabel}</h3>
          <p className="settings-muted">
            {row.node ? 'Local Node: ' + row.node.label : 'No Local Node selected'}
          </p>
        </div>
        <span className="badge" data-state={row.state}>{row.state}</span>
      </div>

      {row.state === 'stale' ? (
        <p>{lastCheckedCopy(row)}</p>
      ) : row.state === 'unverifiable' ? (
        <p>Auteur can&apos;t confirm the sign-in.</p>
      ) : (
        <p>{STATE_LABELS[row.state]}</p>
      )}

      {noNode ? (
        <p className="notice">
          Subscription sign-in runs through the provider&apos;s official app on your computer. A paired Local Node
          is required; sign-in details stay on that computer.{' '}
          {pairingInNewTab ? (
            // A plain anchor: wouter's Link always navigates in this tab, whatever `target` says, which
            // left Welcome behind with no way back (F-17).
            <a href="/devices/pair" target="_blank" rel="noopener noreferrer">Pair a Local Node</a>
          ) : (
            <Link to="/devices/pair">Pair a Local Node</Link>
          )}
          {pairingInNewTab ? ' in another tab, then return here.' : '.'}
        </p>
      ) : null}

      <div className="subscription-quota">
        <h4>Quota</h4>
        {row.quota_status === 'measured' && row.quota?.windows?.length ? (
          <ul>
            {row.quota.windows.map((window) => (
              <li key={window.kind}>
                {window.kind}: {Math.round(window.percent_used)}% used
                {window.resets_at ? ' · resets ' + new Date(window.resets_at).toLocaleString() : ''}
              </li>
            ))}
          </ul>
        ) : (
          <p>Not measured</p>
        )}
      </div>

      {attempt ? (
        <div className="subscription-attempt" aria-live="polite">
          <p>Sign-in attempt: {attempt.status}</p>
          {attempt.status === 'awaiting_user' && attempt.relay ? (
            <div className="subscription-code-panel">
              {verificationLink ? (
                <a href={verificationLink} target="_blank" rel="noopener noreferrer">
                  Open the sign-in page
                </a>
              ) : (
                <p role="alert">The sign-in link did not match this lane&apos;s approved hosts.</p>
              )}
              <p>Enter this code on the sign-in page:</p>
              <p className="subscription-user-code">{attempt.relay.user_code}</p>
              <p>Code expires {new Date(attempt.relay.expires_at).toLocaleString()}.</p>
            </div>
          ) : null}
          {attempt.status === 'finished' ? (
            <p>The Local Node completed the sign-in step. Checking the lane status now.</p>
          ) : null}
        </div>
      ) : null}

      <div className="settings-actions">
        <button
          type="button"
          disabled={busy || noNode || !row.enabled || row.state === 'signing_in'}
          onClick={() => onSignIn(row.lane)}
        >
          Sign in
        </button>
        <button
          type="button"
          className="secondary"
          disabled={busy || noNode}
          onClick={() => onSignOut(row.lane)}
        >
          Sign out
        </button>
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={() => onToggleConnection(row.lane, !row.enabled)}
        >
          {row.enabled ? 'Disconnect' : 'Reconnect'}
        </button>
      </div>
    </article>
  );
}

export function SubscriptionsPanel({ pairingInNewTab = false }: { pairingInNewTab?: boolean } = {}) {
  const [connections, setConnections] = useState<ConnectionRow[] | null>(null);
  const [attempts, setAttempts] = useState<Partial<Record<SubscriptionLane, SignInAttempt>>>({});
  const [busyLane, setBusyLane] = useState<SubscriptionLane | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());

  const refresh = useCallback(async () => {
    try {
      const rows = await listConnections();
      setConnections(rows);
      setError('');
    } catch (failure) {
      setConnections(null);
      setError(failure instanceof Error ? failure.message : 'Subscription status could not be loaded.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
      void refresh();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    function refreshWhenWindowReturns() {
      void refresh();
    }
    window.addEventListener('focus', refreshWhenWindowReturns);
    return () => window.removeEventListener('focus', refreshWhenWindowReturns);
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];

    async function poll(lane: SubscriptionLane, attemptId: string) {
      try {
        const updated = await readConnectionSignIn(lane, attemptId);
        if (cancelled) return;
        setAttempts((current) => ({ ...current, [lane]: updated }));
        if (isPolling(updated.status)) {
          timers.push(window.setTimeout(() => void poll(lane, attemptId), 3000));
        } else if (updated.status === 'finished') {
          await refresh();
        }
      } catch (failure) {
        if (cancelled) return;
        setError(failure instanceof Error ? failure.message : 'Sign-in progress could not be checked.');
        timers.push(window.setTimeout(() => void poll(lane, attemptId), 3000));
      }
    }

    LANES.forEach(({ id }) => {
      const attempt = attempts[id];
      if (attempt && isPolling(attempt.status)) {
        timers.push(window.setTimeout(() => void poll(id, attempt.attempt_id), 3000));
      }
    });
    return () => {
      cancelled = true;
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [attempts, refresh]);

  async function runLaneAction(
    lane: SubscriptionLane,
    action: 'sign-in' | 'sign-out' | 'disconnect' | 'reconnect',
  ) {
    setError('');
    setBusyLane(lane);
    try {
      if (action === 'sign-in') {
        const started = await startConnectionSignIn(lane);
        setAttempts((current) => ({ ...current, [lane]: started.attempt }));
        setConnections((current) => current
          ? current.map((row) => row.lane === lane ? started.connection : row)
          : current);
      } else if (action === 'sign-out') {
        await signOutOfConnection(lane);
        await refresh();
      } else {
        const updated = action === 'disconnect'
          ? await disconnectConnection(lane)
          : await reconnectConnection(lane);
        setConnections((current) => current
          ? current.map((row) => row.lane === lane ? updated : row)
          : current);
        if (action === 'disconnect') {
          setAttempts((current) => ({ ...current, [lane]: undefined }));
        }
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'The subscription action could not be completed.');
    } finally {
      setBusyLane(null);
    }
  }

  return (
    <section className="settings-panel-content">
      <h3>Subscriptions</h3>
      <p>
        These are consumer CLI sign-ins on a paired Local Node. They are separate from your Auteur
        billing subscription.
      </p>
      {error ? <p className="error" role="alert">{error}</p> : null}
      {connections === null ? (
        <p role="status">{error ? 'Subscription status is unavailable.' : 'Loading subscription status…'}</p>
      ) : (
        <div className="settings-card-list">
          {LANES.map(({ id }) => {
            const row = connections.find((connection) => connection.lane === id);
            return row ? (
              <LaneCard
                key={id}
                row={visibleConnection(row, now)}
                attempt={attempts[id]}
                busy={busyLane === id}
                pairingInNewTab={pairingInNewTab}
                onSignIn={(lane) => void runLaneAction(lane, 'sign-in')}
                onSignOut={(lane) => void runLaneAction(lane, 'sign-out')}
                onToggleConnection={(lane, enabled) => {
                  void runLaneAction(lane, enabled ? 'reconnect' : 'disconnect');
                }}
              />
            ) : (
              <article className="settings-card" key={id}>
                <h3>{LANES.find((lane) => lane.id === id)?.label || id}</h3>
                <p role="alert">The API did not return this subscription lane.</p>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
